import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, ChangeDetectionStrategy, ChangeDetectorRef, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { CsRollerListInterface } from './cs-roller-list-interface';

@Component({
  selector: 'cs-roller',
  templateUrl: 'cs-roller.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CsRollerComponent implements OnInit, OnChanges, AfterViewInit {

  // ---  global constants
  readonly bufferSize = 20;   // Buffer items above/below viewport

  // ---  general configuration attributes
  @Input() visibleItems: number = 5;
  @Input() itemHeight: number = 30;
  @Input() alignment: 'center' | 'left' | 'right' = 'center';

  // ---  adapter that provides data to show in the virtual list
  @Input() dataProvider: CsRollerListInterface;

  // ---  two-way bound current index
  @Input() currentIndex: number = 0;
  @Output() currentIndexChange = new EventEmitter<number>();

  // ---  internals
  @ViewChild('scrollContainer') scrollContainer: ElementRef;
  // Track last index to prevent duplicate events
  private lastIndex: number = -1;
  // Flag to indicate smooth scrolling is in progress from click
  private forcedScroll: boolean = false;
  // offset of the physical items from the top of scroll container
  physicalItemsOffsetPx = 0;
  // array of numbers that maps physical items to actual rows in data - used in the template
  physicalItemIndices: number[] = [];
  // TrackBy function for ngFor - track by physical index for DOM stability
  trackByIndex = (index: number): number => this.physicalItemIndices[index];

  // --- constants derived from the inputs

  // Total number of data elements (from the data provider)
  numDataElements: number;
  // offset of the selected (center) row from the top of viewable area
  centerRowOffset: number;
  // Total rows in scrollable area (includes padding by centerRowOffset on both sides!)
  numScrollRows: number;
  // size of scrollable area in pixels
  totalScrollHeightPx: number;

  constructor(
    private cdr: ChangeDetectorRef,
  ) {}

  // --- conversion/calculation helpers

  // calculate the center item index (in the data!) from the scroll position
  currentRowFromScrollTop(scrollTop: number): number {
    // how many rows fall out of the top of the viewable area
    const topRowOffset = Math.round(scrollTop / this.itemHeight);

    // this is essentially + centerRowOffset - numPaddingItems
    // but we don't even store numPaddingItems because it == centerRowOffset
    return topRowOffset;
  }

  // calculate the scroll position needed to center a given data item
  // this is inverse of currentRowFromScrollTop()
  scrollTopForCurrentRow(currentRow: number): number {
    return currentRow * this.itemHeight;
  }


  // prepare all constants that only depend on fixed inputs, not on current scrolling/selection
  calculateConstants() {
    this.numDataElements = this.dataProvider ? this.dataProvider.getTotalElements() : 0;
    this.centerRowOffset = Math.max(Math.floor((this.visibleItems - 1) / 2), 0);
    this.numScrollRows = this.numDataElements + this.centerRowOffset * 2;
    this.totalScrollHeightPx = this.numScrollRows * this.itemHeight;
  }

  // set up data to position the physical items properly to stand inside the viewport
  updateViewPort() {
    // in a general case, we want to have:
    //   bufferSize items above the viewport
    //   visibleItems items in the viewport
    //   bufferSize items below the viewport
    // so total of:
    let numPhysicalItems = this.bufferSize + this.visibleItems + this.bufferSize;
    // and in that case the first physical item maps to actual data item:
    let physicalToDataOffset = this.currentIndex - this.centerRowOffset - 1 - this.bufferSize;

    // find out at which row position we should start placing physical items
    let startRow = this.currentIndex - this.centerRowOffset - this.bufferSize;

    // if it would start before the beginning of the scroll area, trim it
    if (startRow < 0) {
      physicalToDataOffset -= startRow;
      startRow = 0;
    }

    // if it would end after the end of the scroll area, trim it
    const maxPhysicalCount = this.numScrollRows - startRow;
    if (numPhysicalItems > maxPhysicalCount) {
      numPhysicalItems = maxPhysicalCount;
    }

    // now we have the actual start row and number of physical items to show
    // set up the offset and the mapping array

    this.physicalItemsOffsetPx = startRow * this.itemHeight;
    this.physicalItemIndices = Array.from({ length: numPhysicalItems }, (_, i) => i + startRow - this.centerRowOffset);
  }

  /* This is called by the template for every item in physicalItemIndices to get it's label.
  */
  getItemContent(itemIndex: number): string {
    if (!this.dataProvider) return '';

    // return empty for padding items
    if (itemIndex < 0 || itemIndex >= this.numDataElements) {
      return '';
    }

    return this.dataProvider.getElement(itemIndex);
  }


  /**
   * Central synchronization function that determines whether to:
   * 1. Scroll to match an external index change, or
   * 2. Update the index to match the current scroll position
   */
  private syncIndexAndScroll(): void {
    if (!this.scrollContainer || !this.dataProvider) return;

    const scrollElement = this.scrollContainer.nativeElement;
    const scrollTop = scrollElement.scrollTop;

    // Calculate what index the current scroll position represents
    const scrolledCurrentRow = this.currentRowFromScrollTop(scrollTop);

    // If forced scroll is active
    if (this.forcedScroll) {
      // Check if we've reached the target
      if (scrolledCurrentRow === this.currentIndex) {
        // We've reached the target, disable forced scroll
        this.forcedScroll = false;
        this.lastIndex = this.currentIndex;
      }
      // During forced scroll, don't sync from scroll position to index
      // but still update viewport for smooth scrolling
      this.updateViewPort();
      this.cdr.markForCheck();
      return;
    }

    // if currentIndex has changed externally
    if (this.currentIndex !== this.lastIndex) {
      // accept the external change
      this.lastIndex = this.currentIndex;

      // scroll to the new index
      const newScrollTop = this.scrollTopForCurrentRow(this.currentIndex);
      scrollElement.scrollTo({ top: newScrollTop, behavior: 'instant' });


      // reposition the physical items
      this.updateViewPort();

      this.cdr.markForCheck();

      // not changed externally, but the scroll position changed (user scrolled)
    } else if (scrolledCurrentRow !== this.lastIndex) {
      // update our internal state and emit
      this.currentIndex = scrolledCurrentRow;  // Update our local copy
      this.lastIndex = scrolledCurrentRow;     // Track what we set
      this.currentIndexChange.emit(scrolledCurrentRow);  // Notify parent

      // reposition the physical items
      this.updateViewPort();

      this.cdr.markForCheck();
    }
    // If validScrollIndex === lastIndex === currentIndex, everything is in sync
  }

  ngOnInit() {
    this.calculateConstants();
  }

  ngAfterViewInit() {
    // Now scrollContainer is available, do the initial sync
    this.syncIndexAndScroll();
  }

  ngOnChanges(changes: SimpleChanges): void {
    void changes;
    this.calculateConstants();
    this.syncIndexAndScroll();
  }

  onScroll(): void {
    this.syncIndexAndScroll();
  }

  onItemMouseDown(event: Event): void {
    // Prevent mousedown from causing focus loss on the input
    event.preventDefault();
  }

  onItemClick(itemIndex: number, event: Event): void {
    // Prevent the click from propagating up (which might close the keyboard)
    event.stopPropagation();
    event.preventDefault();

    // Ignore clicks on padding items
    if (itemIndex < 0 || itemIndex >= this.numDataElements) {
      return;
    }

    // Set forced scroll flag to enable smooth scrolling
    this.forcedScroll = true;

    // Manually scroll smoothly to the target
    const scrollElement = this.scrollContainer.nativeElement;
    const newScrollTop = this.scrollTopForCurrentRow(itemIndex);
    scrollElement.scrollTo({ top: newScrollTop, behavior: 'smooth' });

    // Update index and emit change
    this.currentIndex = itemIndex;
    this.currentIndexChange.emit(itemIndex);
  }

}